import React from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

/**
 * Helper to check if user has a role (handles author/creator equivalence)
 */
const hasRole = (userRoles, role) => {
  if (!userRoles || !Array.isArray(userRoles)) return false;
  // "author" and "creator" are equivalent (creator is legacy)
  if (role === "author") {
    return userRoles.includes("author") || userRoles.includes("creator");
  }
  return userRoles.includes(role);
};

const ProtectedRoute = ({ children, requiredRole, requireRole }) => {
  const { isAuthenticated, loading, user, backendAvailable } = useAuth();
  
  // Support both prop names for backwards compatibility
  const roleRequired = requiredRole || requireRole;

  // Show loading state while checking auth
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-950 text-slate-100">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-t-indigo-500 border-r-transparent border-b-purple-500 border-l-transparent"></div>
          <p className="mt-4 text-sm text-slate-300">Checking your session…</p>
          {!backendAvailable && (
            <p className="mt-2 text-xs text-amber-400">Backend is starting up...</p>
          )}
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Role-based access control
  if (roleRequired) {
    const userRoles = user?.roles || [];
    if (!hasRole(userRoles, roleRequired)) {
      // User doesn't have required role - redirect silently (navigation is the feedback)
      
      // Redirect to appropriate home based on their actual role
      if (hasRole(userRoles, "admin")) {
        return <Navigate to="/admin" replace />;
      } else if (hasRole(userRoles, "publisher")) {
        return <Navigate to="/publisher" replace />;
      } else if (hasRole(userRoles, "author")) {
        return <Navigate to="/author" replace />;
      } else {
        return <Navigate to="/home" replace />;
      }
    }
  }

  return children;
};

export default ProtectedRoute;
